import { 
  collection, 
  getDocs, 
  getDoc, 
  doc, 
  addDoc, 
  updateDoc, 
  deleteDoc, 
  query, 
  where, 
  orderBy, 
  limit,
  Timestamp 
} from "firebase/firestore";
import { db } from "../firebase/firebase";

export interface QridRecord {
  id: string;
  // Exact field names from Firestore
  Name?: string;
  OrderID?: string;
  "Contact Number"?: string;
  "Pickup Date"?: string;  
  "Return Date"?: string; 
  Status?: string;
  Qrid?: string;
  Timestamp?: any;
  
  // Normalized field names for our app
  Contact_Number?: string; // Mapped from "Contact Number"
  Pickup_Date?: string;    // Mapped from "Pickup Date" 
  Return_Date?: string;    // Mapped from "Return Date"
}

// Get all QridRecords with optional filtering and pagination
export const getQridRecords = async (filterOptions: any = {}, pageSize = 20) => {
  try {
    // Build query
    let qridQuery = query(
      collection(db, "QridRecord"),
      orderBy("Timestamp", "desc"),
      limit(pageSize)
    );
    
    // Log query for debugging
    console.log("Querying QridRecord collection with options:", filterOptions);
    
    // Apply status filter if provided
    if (filterOptions.status && filterOptions.status !== 'all') {
      qridQuery = query(
        collection(db, "QridRecord"),
        where("Status", "==", filterOptions.status),
        orderBy("Timestamp", "desc"),
        limit(pageSize)
      );
    }
    
    // Apply name search if provided
    if (filterOptions.name) {
      const searchTerm = filterOptions.name.toLowerCase();
      // Note: Firestore doesn't support direct "startsWith" queries
      // We'll fetch more results then filter client-side for exact matches
      qridQuery = query(
        collection(db, "QridRecord"),
        orderBy("Name"),
        limit(pageSize * 2)
      );
      
      const querySnapshot = await getDocs(qridQuery);
      const records: QridRecord[] = [];
      
      querySnapshot.forEach((doc) => {
        const data = doc.data();
        if (data.Name && data.Name.toLowerCase().startsWith(searchTerm)) {
          records.push({
            id: doc.id,
            ...data
          } as QridRecord);
        }
      });
      
      return records;
    }
    
    // Execute the query
    const querySnapshot = await getDocs(qridQuery);
    
    const records: QridRecord[] = [];
    querySnapshot.forEach((doc) => {
      records.push({
        id: doc.id,
        ...doc.data()
      } as QridRecord);
    });
    
    return records;
  } catch (error) {
    console.error("Error getting QridRecords:", error);
    throw error;
  }
};

// Get a single QridRecord by ID
export const getQridRecordById = async (id: string) => {
  try {
    const docRef = doc(db, "QridRecord", id);
    const docSnap = await getDoc(docRef);
    
    if (docSnap.exists()) {
      return {
        id: docSnap.id,
        ...docSnap.data()
      } as QridRecord;
    } else {
      return null;
    }
  } catch (error) {
    console.error("Error getting QridRecord:", error);
    throw error;
  }
};

// Add a new QridRecord
export const addQridRecord = async (recordData: Omit<QridRecord, 'id'>) => {
  try {
    // Add timestamp if not provided
    if (!recordData.Timestamp) {
      recordData.Timestamp = new Date();
    }
    
    const docRef = await addDoc(collection(db, "QridRecord"), recordData);
    return docRef.id;
  } catch (error) {
    console.error("Error adding QridRecord:", error);
    throw error;
  }
};

// Update an existing QridRecord
export const updateQridRecord = async (id: string, recordData: Partial<QridRecord>) => {
  try {
    const docRef = doc(db, "QridRecord", id);
    await updateDoc(docRef, recordData);
    return true;
  } catch (error) {
    console.error("Error updating QridRecord:", error);
    throw error;
  }
};

// Delete a QridRecord
export const deleteQridRecord = async (id: string) => {
  try {
    const docRef = doc(db, "QridRecord", id);
    await deleteDoc(docRef);
    return true;
  } catch (error) {
    console.error("Error deleting QridRecord:", error);
    throw error;
  }
};

// Get QridRecord statistics
export const getQridStats = async () => {
  try {
    // Total records
    const totalSnap = await getDocs(collection(db, "QridRecord"));
    const total = totalSnap.size;
    
    // Completed records
    const completedSnap = await getDocs(
      query(collection(db, "QridRecord"), where("Status", "==", "Completed"))
    );
    const completed = completedSnap.size;
    
    // Pending records
    const pendingSnap = await getDocs(
      query(collection(db, "QridRecord"), where("Status", "==", "Pending Return"))
    );
    const pending = pendingSnap.size;
    
    return {
      total,
      completed,
      pending,
    };
  } catch (error) {
    console.error("Error getting QridRecord stats:", error);
    throw error;
  }
};

// Define store action interface
export interface StoreAction {
  id?: string;
  actionType: 'Pickup' | 'Return' | 'Assist';
  name: string;
  status: string;
  Qrid: string;
  who_collect?: string;
  timestamp: any;
  actionstatus?: 'Pending' | 'In Progress' | 'Completed';
}

// Add a store action for QR code pickup or return
export const addStoreAction = async (actionData: Omit<StoreAction, 'id'>) => {
  try {
    // Ensure we have a timestamp
    if (!actionData.timestamp) {
      actionData.timestamp = Timestamp.now();
    }
    
    // Add the action to the StoreActions collection ONLY
    const docRef = await addDoc(collection(db, "StoreActions"), actionData);
    return docRef.id;
  } catch (error) {
    console.error("Error adding store action:", error);
    throw error;
  }
};

// Get recent store actions
export const getRecentStoreActions = async (limitCount = 10) => {
  try {
    const storeActionsQuery = query(
      collection(db, "StoreActions"),
      orderBy("timestamp", "desc"),
      limit(limitCount)
    );
    
    const querySnapshot = await getDocs(storeActionsQuery);
    
    const actions: StoreAction[] = [];
    querySnapshot.forEach((doc) => {
      actions.push({
        id: doc.id,
        ...doc.data()
      } as StoreAction);
    });
    
    return actions;
  } catch (error) {
    console.error("Error getting recent store actions:", error);
    throw error;
  }
}; 